import { useEffect, useMemo, useState } from "react";

import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection } from "../../../Components/Form";
import { setting_image_url } from "../../../Components/ImageUrls";
import { ToastMessage } from "../../../Components/Utils";
import { useFetchLatestWebsiteSettingsQuery, useSaveWebsiteSettingsMutation } from "../../../stores/api/settings.api";
import settingsHelper from "../../../stores/api/helpers/settings.helper";

const getImgUrl = (path) => {
  if (!path) return null;
  const filename = path.split("/").pop();
  return `${setting_image_url}${filename}`;
};

const EMPTY = {
  website_name: "",
  footer_about: "",
  header_logo: null,
  footer_logo: null,
  home_about_image:null,
  dark_header_logo: null,
  dark_footer_logo: null,
  favicon: null,
};

const IMAGE_FIELDS = ["header_logo","home_about_image", "footer_logo", "dark_header_logo", "dark_footer_logo", "favicon"];

const WebsiteSettings = () => {
  const [formData, setFormData] = useState(EMPTY);
  const { data: fetched, isLoading } = useFetchLatestWebsiteSettingsQuery();
  const [saveSettings, { isLoading: isSaving }] = useSaveWebsiteSettingsMutation();

  useEffect(() => {
    if (fetched) {
      setFormData({
        website_name: fetched.website_name || "",
        footer_about: fetched.footer_about || "",
        header_logo: null,
        footer_logo: null,
        home_about_image:null,
        dark_header_logo: null,
        dark_footer_logo: null,
        favicon: null,
      });
    }
  }, [fetched]);

  const previewUrls = useMemo(() => {
    if (!fetched) return {};
    const urls = {};
    IMAGE_FIELDS.forEach((field) => {
      if (fetched[field]) {
        urls[field] = getImgUrl(fetched[field]);
      }
    });
    return urls;
  }, [fetched]);

  const setField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (field) => (file) => {
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = settingsHelper.forSave(formData);
      await saveSettings(payload).unwrap();
      ToastMessage({ type: "success", title: "Success", message: "Settings saved!" });
    } catch (err) {
      ToastMessage({ type: "error", title: "Error!", message: err?.data?.message || err?.message || "Something went wrong" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="General">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-1">
            <FormInput
            className="w-full"
              label="Website Name" name="website_name" value={formData.website_name} isRequired
              onChange={(val) => setField("website_name", val)}
              placeholder="e.g. Ukasha Enterprise"
            />
            <FormInput
              label="Footer About" name="footer_about" value={formData.footer_about} isRequired
              onChange={(val) => setField("footer_about", val)}
              placeholder="Short about text shown in footer" rows={3}
              className="md:col-span-2"
            />
          </div>
        </FormSection>

        <FormSection title="Logos — Light Mode">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormImage
              label="Header Logo"
              initialPreviewUrl={previewUrls.header_logo}
              onChange={handleImageChange("header_logo")}
            />
            <FormImage
              label="Footer Logo"
              initialPreviewUrl={previewUrls.footer_logo}
              onChange={handleImageChange("footer_logo")}
            />
          </div>
        </FormSection>

        <FormSection title="Logos — Dark Mode">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormImage
              label="Dark Header Logo"
              initialPreviewUrl={previewUrls.dark_header_logo}
              onChange={handleImageChange("dark_header_logo")}
            />
            <FormImage
              label="Dark Footer Logo"
              initialPreviewUrl={previewUrls.dark_footer_logo}
              onChange={handleImageChange("dark_footer_logo")}
            />
          </div>
        </FormSection>

        <FormSection title="Favicon">
          <div className="max-w-sm">
            <FormImage
              label="Favicon (32×32 or 64×64 recommended)"
              initialPreviewUrl={previewUrls.favicon}
              onChange={handleImageChange("favicon")}
            />
          </div>
        </FormSection>
        <FormSection title="Home About Image">
          <div className="max-w-sm">
            <FormImage
              label="Home About Image (32×32 or 64×64 recommended)"
              initialPreviewUrl={previewUrls.home_about_image}
              onChange={handleImageChange("home_about_image")}
            />
          </div>
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="submit" loading={isSaving} disabled={isSaving}
            className="!bg-orange-500 !text-white hover:!bg-orange-600"
            loadingText="Saving..."
          >
            Save Settings
          </Button>
        </div>
    </form>
  );
};

export default WebsiteSettings;
